// ============================================================================
// BOQ — Measurement sheet (take-off) XLSX export / import, with live formulas
// ============================================================================
// Export writes the four measure columns as real Excel formulas referencing
// the Nr/L/B/H cells, so editing dimensions in Excel recomputes the measures
// live. IF-guards return blank when a measure's dimensions aren't all present,
// matching the app's NULL-not-zero behaviour. A SUM total row is included.
//
// Import reads only the geometry + location inputs (Nr/L/B/H, zone/level/grid/
// note); the four measures are re-derived by the DB generated columns, so the
// formula columns in the file are ignored on the way back in.
//
// Column layout (1-indexed):
//   A Description  B Nr  C L  D B  E H
//   F Run m  G Plan m²  H Elev m²  I Vol m³   (formulas)
//   J Zone  K Level  L Grid  M Note
// ============================================================================
import ExcelJS from 'exceljs';

const BRIO_GOLD = 'FF9F902A';
const BRIO_GOLD_LIGHT = 'FFF5F0D0';
const THIN = { style: 'thin', color: { argb: 'FFD1D5DB' } };
const CELL_BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

const HEADERS = ['Description', 'Nr', 'L', 'B', 'H', 'Run m', 'Plan m²', 'Elev m²', 'Vol m³', 'Zone', 'Level', 'Grid', 'Note'];
const WIDTHS  = [44, 8, 9, 9, 9, 11, 11, 11, 11, 22, 10, 9, 30];

const n = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

function zonePath(zoneId, zonesById) {
  if (!zoneId) return '';
  const parts = [];
  let cur = zonesById.get(zoneId);
  while (cur) { parts.unshift(cur.code); cur = cur.parentId ? zonesById.get(cur.parentId) : null; }
  return parts.join(' / ');
}

export async function exportMeasurementSheet({ line, basis, measurements, zonesById }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BRIO QS Pro';
  wb.created = new Date();
  const ws = wb.addWorksheet('Take-off', { views: [{ state: 'frozen', ySplit: 2 }] });

  ws.columns = WIDTHS.map((w) => ({ width: w }));

  // Title row (line code + description + basis)
  ws.mergeCells(1, 1, 1, HEADERS.length);
  const title = ws.getCell(1, 1);
  title.value = `${line.version?.fullCode || ''}  ${line.version?.shortDescription || ''}   ·   bills on: ${basis || 'manual'}`;
  title.font = { bold: true };

  // Header row (row 2)
  const header = ws.getRow(2);
  header.values = HEADERS;
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD } };
  header.height = 20;

  const firstDataRow = 3;
  measurements.forEach((m, i) => {
    const r = firstDataRow + i;
    const row = ws.getRow(r);
    row.getCell(1).value = m.description || '';
    row.getCell(2).value = n(m.nr);
    row.getCell(3).value = n(m.length);
    row.getCell(4).value = n(m.breadth);
    row.getCell(5).value = n(m.height);
    // Formula columns with blank-guards. result keeps the value visible pre-recalc.
    row.getCell(6).value = { formula: `IF(C${r}="","",B${r}*C${r})`, result: m.runningM ?? null };
    row.getCell(7).value = { formula: `IF(OR(C${r}="",D${r}=""),"",B${r}*C${r}*D${r})`, result: m.planArea ?? null };
    row.getCell(8).value = { formula: `IF(OR(C${r}="",E${r}=""),"",B${r}*C${r}*E${r})`, result: m.elevationArea ?? null };
    row.getCell(9).value = { formula: `IF(OR(C${r}="",D${r}="",E${r}=""),"",B${r}*C${r}*D${r}*E${r})`, result: m.volume ?? null };
    row.getCell(10).value = zonePath(m.projectZoneId, zonesById);
    row.getCell(11).value = m.level || '';
    row.getCell(12).value = m.gridRef || '';
    row.getCell(13).value = m.locationNote || '';
    row.eachCell((c) => { c.border = CELL_BORDER; });
    if (i % 2 === 1) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD_LIGHT } };
    for (let col = 6; col <= 9; col++) ws.getCell(r, col).numFmt = '[<10]#,##0.##;[>-10]-#,##0.##;#,##0';
  });

  // Total row with SUM formulas on the four measure columns
  const lastDataRow = firstDataRow + measurements.length - 1;
  if (measurements.length > 0) {
    const tr = lastDataRow + 1;
    ws.getCell(tr, 1).value = 'TOTAL';
    ws.getCell(tr, 1).font = { bold: true };
    [6, 7, 8, 9].forEach((col) => {
      const L = String.fromCharCode(64 + col); // F,G,H,I
      const cell = ws.getCell(tr, col);
      cell.value = { formula: `SUM(${L}${firstDataRow}:${L}${lastDataRow})` };
      cell.font = { bold: true };
      cell.numFmt = '[<10]#,##0.##;[>-10]-#,##0.##;#,##0';
    });
  }

  return wb.xlsx.writeBuffer();
}

export async function parseMeasurementSheet(file, { zonesByPath }) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const ws = wb.getWorksheet('Take-off') || wb.worksheets[0];
  if (!ws) return { rows: [], errors: [{ row: 0, message: 'No worksheet found.' }] };

  // Header is on row 2 (row 1 is the title). Find it defensively.
  let headerRowIdx = 2;
  for (let r = 1; r <= 3; r++) {
    const first = String(ws.getRow(r).getCell(1).value || '').trim().toLowerCase();
    if (first === 'description') { headerRowIdx = r; break; }
  }
  const header = ws.getRow(headerRowIdx);
  const colByName = {};
  header.eachCell((cell, col) => {
    const k = String(cell.value || '').trim().toLowerCase();
    if (k) colByName[k] = col;
  });
  const C = {
    description: colByName['description'] || 1,
    nr: colByName['nr'] || 2,
    length: colByName['l'] || 3,
    breadth: colByName['b'] || 4,
    height: colByName['h'] || 5,
    zone: colByName['zone'] || 10,
    level: colByName['level'] || 11,
    grid: colByName['grid'] || 12,
    note: colByName['note'] || 13,
  };

  const text = (row, col) => {
    const v = row.getCell(col).value;
    if (v === null || v === undefined) return '';
    if (typeof v === 'object' && 'result' in v) return String(v.result ?? '').trim();
    if (typeof v === 'object' && 'richText' in v) return v.richText.map((p) => p.text).join('').trim();
    if (typeof v === 'object' && 'text' in v) return String(v.text ?? '').trim();
    return String(v).trim();
  };
  const number = (row, col) => {
    const t = text(row, col);
    if (t === '') return null;
    const cleaned = t.replace(/[,$€£₹\s]/g, '');
    const num = Number(cleaned);
    return isNaN(num) ? null : num;
  };

  const rows = [];
  const errors = [];
  for (let r = headerRowIdx + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    if (!row.hasValues) continue;
    const desc = text(row, C.description);
    if (desc.toUpperCase() === 'TOTAL') continue;

    const nr = number(row, C.nr);
    const length = number(row, C.length);
    const breadth = number(row, C.breadth);
    const height = number(row, C.height);
    // Skip a fully blank row
    if (!desc && nr === null && length === null && breadth === null && height === null) continue;

    const rowErrors = [];
    if (nr !== null && nr === 0) rowErrors.push('Nr cannot be 0.');

    const zoneTxt = text(row, C.zone);
    let projectZoneId = null;
    if (zoneTxt) {
      projectZoneId = zonesByPath.get(zoneTxt) || null;
      if (!projectZoneId) rowErrors.push(`Unknown zone "${zoneTxt}".`);
    }

    rows.push({
      rowNumber: r,
      description: desc || null,
      nr: nr ?? 1,
      length,
      breadth,
      height,
      projectZoneId,
      zonePath: zoneTxt,
      level: text(row, C.level) || null,
      gridRef: text(row, C.grid) || null,
      locationNote: text(row, C.note) || null,
      errors: rowErrors,
    });
    if (rowErrors.length) errors.push({ row: r, message: rowErrors.join(' · ') });
  }

  return { rows, errors };
}

// Zone-path lookup keyed by full path and by leaf code (when unambiguous).
export function buildZonePathLookup(zonesById) {
  const map = new Map();
  const path = (id) => zonePath(id, zonesById);
  zonesById.forEach((z) => {
    map.set(path(z.id), z.id);
    if (!map.has(z.code)) map.set(z.code, z.id);
  });
  return map;
}
